// Webinar thumbnails (Adam, 9 Oct: "the webinars need to have like a professional thumbnail which I
// can design myself"). Each is Adam's own 16:9 design in the private covers bucket, shown to members
// through a link sign-file signs for 2 minutes ({"webinars": [ids]}, read with the member's own token,
// so row level security decides which come back). The Library and Free webinars share this:
// - sign-file is asked only when at least 1 webinar shown has a thumbnail, at most 20 ids a call;
// - each link is kept here by its path for 100 seconds, inside the link's 2 minutes, so going from the
//   Library to Free webinars and back doesn't ask again;
// - a picture already on the screen keeps its link: it's never signed again just because the list
//   changed, so it never blinks, and only that link failing to load takes it off;
// - a call sign-file doesn't answer (no answer, or a 5xx) is made once more; if it still isn't answered,
//   or it's refused, the pictures showing stay and those still waiting get the date panel. An answer
//   from an older call for a path asked again since is let go;
// - an image that won't load (its link ran out, say) is signed once more; if it fails again, or
//   sign-file answers and signs none for it, the card draws its date panel instead (Thumbnail.jsx). A
//   picture that loads can be signed once more again later, when its new link runs out too.
// Staff viewing as a member are asked for like anyone (Adam, 10 Oct: View as shows what the member
// sees): sign-file reads the webinars with their own login, which reads as the member, and records
// nothing.
// Since 12 Oct (decision 47) every course recording can have a thumbnail too, exactly as a webinar's:
// the same rules, with sign-file's recordings target ({"recordings": [ids]}) in place of webinars'.
// target says which: 'webinars' (the default, as the Library and Free webinars always asked),
// 'recordings' (product/recordingThumbs.js) or 'covers' (3 Nov: a free product's own cover photo on
// Free resources' card and the Library's Free resources strip, sign-file's {"covers": [product ids]},
// whose answer is under covers, not thumbnails). Each target keeps its own links.
import { useCallback, useEffect, useRef, useState } from 'react'
import { callHubFunction } from '../lib/functions.js'
import { batches, wanted, split, merge } from './thumbnailRules.js'

export { BATCH, KEEP_MS } from './thumbnailRules.js'

// target -> path -> { url, at }
const keptBy = { webinars: new Map(), recordings: new Map(), covers: new Map() }
const keptFor = target => keptBy[target] || keptBy.webinars
export const TARGETS = Object.keys(keptBy)

const signFile = body => callHubFunction('sign-file', body)

// no answer at all (status 0), or the function or its storage failing (a 5xx): worth asking once more
const unreachable = e => !e?.status || e.status >= 500

// 2 links the same once the browser has written them out in full (an <img>'s src is)
function sameLink(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false
  try { return new URL(a, window.location.href).href === new URL(b, window.location.href).href } catch { return a === b }
}

// sign-file's answer for 1 batch, asked once more when it doesn't answer; null if it still doesn't, or
// refuses (a 4xx is about the caller, not the pictures)
async function signBatch(call, ids, target) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return (await call({ [target]: ids })) || null
    } catch (e) {
      if (!unreachable(e)) return null
    }
  }
  return null
}

// Signs items ({ id, path }) in batches of 20: { <path>: url, or false where sign-file answered and
// signed none for it }. A batch it didn't answer or refused leaves its paths out.
export async function signThumbnails(items, call = signFile, target = 'webinars') {
  const out = {}
  const kept = keptFor(target)
  await Promise.all(batches(items).map(async group => {
    const answer = await signBatch(call, group.map(i => i.id), target)
    if (!answer) return
    const got = (target === 'covers' ? answer.covers : answer.thumbnails) || {}
    for (const i of group) {
      const url = got[i.id]?.url
      if (typeof url !== 'string' || !url) { out[i.path] = false; continue }
      out[i.path] = url
      kept.set(i.path, { url, at: Date.now() })
    }
  }))
  return out
}

// For a list of webinars (each { id, thumbnailPath }): frame(w) gives { url, pending } for its
// ThumbnailFrame; failed(w, src) is the frame's onError and loaded(w, src) its onLoad, each with the
// link: a link already replaced (the card's and the player's picture failing together) is let go, so
// it's signed once more only once. With target 'recordings', the same for recordings.
export function useThumbnails(webinars, target = 'webinars') {
  const kept = keptFor(target)
  const items = wanted(webinars)
  const key = items.map(i => `${i.id}:${i.path}`).join(',')
  // path -> url, or false where the date panel shows; a path not here yet is still being signed
  const [urls, setUrls] = useState({})
  const current = useRef(urls)
  current.current = urls
  // path -> the number of the newest call that asked for it, while it's out
  const asking = useRef(new Map())
  const calls = useRef(0)
  // paths signed once more after their link failed, until a picture for them loads
  const retried = useRef(new Set())
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => { live.current = false }
  }, [])

  const ask = useCallback(list => {
    const n = ++calls.current
    for (const i of list) asking.current.set(i.path, n)
    signThumbnails(list, signFile, target).then(got => {
      if (!live.current) return
      // only the paths this is still the newest call for
      const mine = list.map(i => i.path).filter(p => asking.current.get(p) === n)
      for (const p of mine) asking.current.delete(p)
      if (!mine.length) return
      current.current = merge(current.current, mine, got)
      setUrls(u => merge(u, mine, got))
    })
  }, [target])

  useEffect(() => {
    if (!items.length) return
    // a picture already on the screen keeps its link, however long it's been kept
    const shown = path => typeof current.current[path] === 'string'
    const { ready, need } = split(items, Date.now(), kept)
    const fresh = Object.entries(ready).filter(([path]) => !shown(path))
    if (fresh.length) {
      setUrls(u => {
        const next = { ...u }
        for (const [path, url] of fresh) if (typeof next[path] !== 'string') next[path] = url
        return next
      })
    }
    const ahead = need.filter(i => !shown(i.path))
    if (ahead.length) ask(ahead)
  }, [key])

  const failed = useCallback((w, src) => {
    const path = w?.thumbnailPath
    const now = path ? current.current[path] : null
    // a link already let go (the card's and the player's picture failing together count once)
    if (typeof now !== 'string' || (src && !sameLink(now, src))) return
    kept.delete(path)
    if (retried.current.has(path)) {
      current.current = { ...current.current, [path]: false }
      setUrls(u => (u[path] === now ? { ...u, [path]: false } : u))
      return
    }
    // once more, with a new link
    retried.current.add(path)
    const { [path]: _, ...rest } = current.current
    current.current = rest
    setUrls(u => {
      if (u[path] !== now) return u
      const next = { ...u }
      delete next[path]
      return next
    })
    ask([{ id: w.id, path }])
  }, [ask])

  // a picture that loads: should its link run out later in the visit, it's signed once more again
  const loaded = useCallback((w, src) => {
    const path = w?.thumbnailPath
    if (path && sameLink(current.current[path], src)) retried.current.delete(path)
  }, [])

  const frame = w => {
    const path = w?.thumbnailPath
    if (!path) return { url: null, pending: false }
    const v = urls[path]
    if (v === undefined) return { url: null, pending: true }
    return { url: v || null, pending: false }
  }
  return { frame, failed, loaded }
}
